import moment from 'moment'
import { filter, kebabCase, isEmpty, last } from 'lodash'

import getImageUrl from 'helpers/image'
import { i18n } from 'i18n-web/i18next'
import { selectCurrencyForCountry } from 'redux-web/selectors/countries'
import { formatAddress } from 'i18n-web/formatters'
import { convertMetersToMiles, convertMetersToKilometers } from 'helpers'
import { getMeasurementSystemFromLocale } from 'models/settings'

export const getCanonicalURL = lot => {
  const citySegment = kebabCase(`${lot?.address?.city}-${lot?.address?.state}`)
  const lotSegment = kebabCase(`${lot?.id}-${lot?.name}`)

  return `https://www.citifyd.com/parking-lots/${citySegment}/${lotSegment}/book`
}

export const getMultiparkingCanonicalURL = lot => {
  const citySegment = kebabCase(`${lot?.address?.city}-${lot?.address?.state}`)
  const lotSegment = kebabCase(`${lot?.id}-${lot?.name}`)

  return `https://www.citifyd.com/parking-lots/${citySegment}/${lotSegment}`
}

export const getCurrentOrNextAvailability = lot =>
  !isEmpty(lot?.upcomingAvailabilities) &&
  filter(lot?.upcomingAvailabilities, availability => {
    const { latestReservationStartAllowed } = availability

    return (
      !latestReservationStartAllowed ||
      moment().isBefore(moment(latestReservationStartAllowed))
    )
  })

export const getCurrentAvailabilityAllocation = (
  reservation,
  returnIfEnded = false
) => {
  if (!reservation) {
    return null
  }

  const { availabilityAllocations } = reservation
  const lastAllocation = last(availabilityAllocations)

  if (!lastAllocation) {
    return null
  }

  const now = moment()
  const allocationStartTime = moment(lastAllocation.startTime)
  const allocationEndTime =
    lastAllocation.endTime && moment(lastAllocation.endTime)

  if (
    now.isSameOrAfter(allocationStartTime) &&
    (returnIfEnded || !allocationEndTime || now.isBefore(allocationEndTime))
  ) {
    return lastAllocation
  }

  return null
}

export const isLotOpen = lot => {
  const currentOrNextAvailabilities = getCurrentOrNextAvailability(lot)
  const now = moment()

  if (currentOrNextAvailabilities[0]) {
    const { start, end } = currentOrNextAvailabilities[0]

    return now.isAfter(moment(start)) && now.isBefore(moment(end))
  }

  return false
}

export const isLotFull = lot => {
  const currentOrNextAvailabilities = getCurrentOrNextAvailability(lot)

  if (currentOrNextAvailabilities?.length > 0) {
    const { spaces } = currentOrNextAvailabilities[0]
    const regularSpaces = spaces.find(({ name }) => name === 'regular')

    return regularSpaces.occupied >= regularSpaces.max
  }

  return false
}

export const getFormattedLot = ({ t, lot, countries, isClosed, isFull }) => {
  if (!lot) return

  const { language } = i18n
  const currency = selectCurrencyForCountry(countries, lot?.countryCode)

  const measurementSystem = getMeasurementSystemFromLocale(language)

  const convertedDistance = lot?.lotDistance?.distance
    ? measurementSystem === 'imperial'
      ? t('event.distance.miles', {
          distance: convertMetersToMiles(lot.lotDistance.distance).toFixed(2),
        })
      : t('event.distance.kilometers', {
          distance: convertMetersToKilometers(lot.lotDistance.distance).toFixed(
            2
          ),
        })
    : null

  const data = {
    ...lot,
    currency: currency || '',
    isClosed,
    isFull,
    image: getImageUrl(lot?.photo, 't_on_demand_map_lot_image_web_2'),
    convertedDistance: convertedDistance,
    availability: getCurrentOrNextAvailability(lot),
    countryCode: lot?.countryCode,
    address: lot?.address,
    addressFormatted: formatAddress(
      lot?.address,
      lot?.countryCode,
      'full_inline'
    ),
    notes: lot?.notes,
    addressLine1: lot?.address.line1,
    isSpacesGuaranteed: lot?.onDemandSpacesGuaranteed,
    isRemindEnabled: lot?.onDemandExpirationReminderEnabled,
    // noSpacesGuaranteedDisclaimer:
    //   !lot.onDemandSpacesGuaranteed && !userIsCloseToLot,
  }

  return data
}
